/**
 * Receivables Analytics — canonical selectors for DSO + AR Aging.
 *
 * Drops two `Beta · est.` stubs in the Finance Command Center by computing
 * directly from `JobFinancials.invoices[]` (real issuedAt / paidAt / dueDate
 * timestamps already present in seed data).
 *
 *   • computeDso()        → amount-weighted days-to-collect, with prior-period delta
 *   • computeArAging()    → 0–30 / 31–60 / 61–90 / 90+ buckets from open invoices
 */
import type { Job, Invoice } from "@/data/types";

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const daysBetween = (a: string, b: string) =>
  Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / MS_PER_DAY));

// ─── DSO ────────────────────────────────────────────────────────────────

export interface DsoResult {
  /** Amount-weighted average days from issue → payment over the window. */
  days: number | null;
  /** Sample size (paid invoices counted). */
  sample: number;
  /** Prior-period DSO for comparison. Null if insufficient sample. */
  priorDays: number | null;
  /** Delta in days (current − prior). Negative = improvement. */
  deltaDays: number | null;
}

/**
 * Compute DSO from paid invoices in the last `windowDays` (default 90).
 * Amount-weighted so a $50K invoice paid in 60 days dominates a $1K invoice paid in 5.
 */
export function computeDso(jobs: Job[], windowDays = 90): DsoResult {
  const now = Date.now();
  const cutoff = now - windowDays * MS_PER_DAY;
  const priorCutoff = now - 2 * windowDays * MS_PER_DAY;

  const collect = (start: number, end: number) => {
    let weightedDays = 0;
    let totalAmount = 0;
    let count = 0;
    for (const job of jobs) {
      for (const inv of job.financials?.invoices ?? []) {
        if (!inv.issuedAt || !inv.paidAt) continue;
        const paidTs = new Date(inv.paidAt).getTime();
        if (paidTs < start || paidTs >= end) continue;
        const d = daysBetween(inv.issuedAt, inv.paidAt);
        weightedDays += d * inv.amount;
        totalAmount += inv.amount;
        count += 1;
      }
    }
    return {
      days: totalAmount > 0 ? Math.round(weightedDays / totalAmount) : null,
      sample: count,
    };
  };

  const current = collect(cutoff, now);
  const prior = collect(priorCutoff, cutoff);

  return {
    days: current.days,
    sample: current.sample,
    priorDays: prior.days,
    deltaDays: current.days != null && prior.days != null ? current.days - prior.days : null,
  };
}

// ─── AR Aging ───────────────────────────────────────────────────────────

export interface ArAgingBucket {
  label: string;
  amount: number;
  count: number;
  tone: "good" | "warn" | "bad";
}

export interface ArAgingResult {
  buckets: ArAgingBucket[];
  totalOpen: number;
}

/**
 * AR Aging from open (unpaid) invoices, bucketed by days since dueDate
 * (falls back to issuedAt + 30 if dueDate is missing).
 */
export function computeArAging(jobs: Job[]): ArAgingResult {
  const now = Date.now();
  const buckets = {
    b1: { label: "0–30 days", amount: 0, count: 0, tone: "good" as const },
    b2: { label: "31–60 days", amount: 0, count: 0, tone: "good" as const },
    b3: { label: "61–90 days", amount: 0, count: 0, tone: "warn" as const },
    b4: { label: "90+ days", amount: 0, count: 0, tone: "bad" as const },
  };

  for (const job of jobs) {
    for (const inv of job.financials?.invoices ?? []) {
      if (inv.status === "paid") continue;
      if (!inv.issuedAt) continue;
      const dueTs = inv.dueDate
        ? new Date(inv.dueDate).getTime()
        : new Date(inv.issuedAt).getTime() + 30 * MS_PER_DAY;
      const ageDays = Math.max(0, Math.round((now - dueTs) / MS_PER_DAY));
      const target =
        ageDays <= 30 ? buckets.b1
        : ageDays <= 60 ? buckets.b2
        : ageDays <= 90 ? buckets.b3
        : buckets.b4;
      target.amount += inv.amount;
      target.count += 1;
    }
  }

  const arr = [buckets.b1, buckets.b2, buckets.b3, buckets.b4];
  return {
    buckets: arr,
    totalOpen: arr.reduce((s, b) => s + b.amount, 0),
  };
}

// ─── Net Margin Delta ───────────────────────────────────────────────────

export interface MarginDelta {
  current: number;
  prior: number | null;
  deltaPp: number | null;
}

/**
 * Net margin % current vs prior 30d window. Uses the earliest invoice
 * `issuedAt` per job as the "sold" timestamp proxy (closest stable signal
 * until a canonical sold-date is wired).
 */
export function computeMarginDelta(jobs: Job[], windowDays = 30): MarginDelta {
  const now = Date.now();
  const cutoff = now - windowDays * MS_PER_DAY;
  const priorCutoff = now - 2 * windowDays * MS_PER_DAY;

  const soldTs = (j: Job): number | null => {
    const invs = j.financials?.invoices ?? [];
    if (invs.length === 0) return null;
    const earliest = invs
      .map((i: Invoice) => (i.issuedAt ? new Date(i.issuedAt).getTime() : null))
      .filter((t): t is number => t != null)
      .sort((a, b) => a - b)[0];
    return earliest ?? null;
  };

  const slice = (start: number, end: number) => {
    let rev = 0;
    let margin = 0;
    for (const j of jobs) {
      if (!j.financials) continue;
      const ts = soldTs(j);
      if (ts == null || ts < start || ts >= end) continue;
      rev += j.financials.contractValue;
      margin += j.financials.marginEstimate;
    }
    return rev > 0 ? (margin / rev) * 100 : null;
  };

  const current = slice(cutoff, now);
  const prior = slice(priorCutoff, cutoff);

  // Fallback: if no jobs sold in current window, use lifetime margin so the
  // headline number is never blank. Delta still null in that case (honest).
  const currentSafe =
    current ??
    (() => {
      const rev = jobs.reduce((s, j) => s + (j.financials?.contractValue ?? 0), 0);
      const m = jobs.reduce((s, j) => s + (j.financials?.marginEstimate ?? 0), 0);
      return rev > 0 ? (m / rev) * 100 : 0;
    })();

  return {
    current: currentSafe,
    prior,
    deltaPp: current != null && prior != null ? current - prior : null,
  };
}

// ─── Collected In Window ────────────────────────────────────────────────

export interface CollectedResult {
  amount: number;
  count: number;
  priorAmount: number;
  deltaPct: number | null;
}

/**
 * Total cash collected (sum of paid-invoice amounts where paidAt falls in window).
 * Comparator is the matched prior window of equal length.
 */
export function computeCollectedInWindow(jobs: Job[], windowDays = 30): CollectedResult {
  const now = Date.now();
  const cutoff = now - windowDays * MS_PER_DAY;
  const priorCutoff = now - 2 * windowDays * MS_PER_DAY;

  let amount = 0;
  let count = 0;
  let priorAmount = 0;
  for (const j of jobs) {
    for (const inv of j.financials?.invoices ?? []) {
      if (!inv.paidAt) continue;
      const ts = new Date(inv.paidAt).getTime();
      if (ts >= cutoff && ts <= now) {
        amount += inv.amount;
        count += 1;
      } else if (ts >= priorCutoff && ts < cutoff) {
        priorAmount += inv.amount;
      }
    }
  }
  const deltaPct = priorAmount > 0 ? ((amount - priorAmount) / priorAmount) * 100 : null;
  return { amount, count, priorAmount, deltaPct };
}

// ─── Top Receivables (open balance by customer) ─────────────────────────

export interface TopReceivable {
  customerName: string;
  openBalance: number;
  invoiceCount: number;
  /** Days since the oldest open invoice's dueDate (0 if not yet due). */
  oldestDueDays: number;
}

/**
 * Top N customers ranked by open (unpaid) invoice balance.
 * Pulls customerName from Job; oldestDueDays drives the urgency tone in UI.
 */
export function computeTopReceivables(jobs: Job[], limit = 5): TopReceivable[] {
  const now = Date.now();
  const byCustomer = new Map<string, TopReceivable>();

  for (const job of jobs) {
    for (const inv of job.financials?.invoices ?? []) {
      if (inv.status === "paid") continue;
      if (!inv.issuedAt) continue;
      const dueTs = inv.dueDate
        ? new Date(inv.dueDate).getTime()
        : new Date(inv.issuedAt).getTime() + 30 * MS_PER_DAY;
      const ageDays = Math.max(0, Math.round((now - dueTs) / MS_PER_DAY));
      const key = job.customerName || job.id;
      const existing = byCustomer.get(key);
      if (existing) {
        existing.openBalance += inv.amount;
        existing.invoiceCount += 1;
        existing.oldestDueDays = Math.max(existing.oldestDueDays, ageDays);
      } else {
        byCustomer.set(key, {
          customerName: job.customerName || "Unknown",
          openBalance: inv.amount,
          invoiceCount: 1,
          oldestDueDays: ageDays,
        });
      }
    }
  }

  return Array.from(byCustomer.values())
    .sort((a, b) => b.openBalance - a.openBalance)
    .slice(0, limit);
}

